import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import KLBusinessOnboardingProgress from "../../components/KLBusinessOnboardingProgress";
import { Container, InfoText, LinkWrapper, Subtitle, TextIconContainer, Title } from "../styles";
import KLButton from "../../components/KLButton";
import KLEventAvailableIcon from "../../components/icons/KLEventAvailableIcon";
import colors from "../../constants/colors";
import {
  successContainerVariants,
  successIconVariants,
  successTitleVariants,
  successSubtitleVariants,
  successInfoVariants,
  successActionVariants,
} from "../../animations/successCelebrations";
import { AvailabilityType } from "../../constants/types";
import { StorageKeys, StorageKey } from "../../constants/localStorage";
import { clearLocalStorage } from "../../utils";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  BusinessCategory,
  CategoryConfig,
  categoryConfigMap,
} from "../../constants/customers/categoryConfigMap";
import { FlowType } from "../../constants/routes";

// Lazy-load confetti for better bundle size
const Confetti = lazy(() => import("react-confetti"));

interface OwnerSuccessScreenProps {
  isOnboarding?: boolean;
}

const OwnerSuccessScreen: React.FC<OwnerSuccessScreenProps> = ({
  isOnboarding,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { width, height } = useWindowSize();
  const reducedMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const { category } = useSelector((state: RootState) => state.businesses);
  const searchParams = new URLSearchParams(location.search);
  const businessId = searchParams.get("businessId");
  const availabilityType = searchParams.get(
    "availabilityType",
  ) as AvailabilityType;

  const link = `https://kavanu.li/${businessId}`;

  const categoryConfig: CategoryConfig | undefined = category
    ? categoryConfigMap[category as BusinessCategory]
    : undefined;

  const term = categoryConfig?.term || "תור";
  const pluralTerm = categoryConfig?.pluralTerm || `${term}ים`;

  const handleCopyLink = (): void => {
    navigator.clipboard.writeText(link);
    setCopied(true);
  };

  const handleGoHome = (): void => {
    navigate(`/${FlowType.Business}/dashboard`);
  };

  useEffect(() => {
    const keysToClear: StorageKey[] = [
      StorageKeys.BusinessId,
      StorageKeys.SelectedService,
      StorageKeys.CurrentStep,
      StorageKeys.MaxStepReached,
      StorageKeys.IsRedirecting,
      StorageKeys.CustomerName,
      StorageKeys.CustomerPhone,
    ];
    clearLocalStorage(keysToClear);

    // Auto-hide confetti after animation completes (3.5 seconds)
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3500);

    return () => clearTimeout(timer);
  }, []);

  const getAvailabilityText = (
    availabilityType: AvailabilityType | null,
  ): string => {
    switch (availabilityType) {
      case AvailabilityType.Weekly:
        return "לשבוע הקרוב";
      case AvailabilityType.BiWeekly:
        return "לשבועיים הקרובים";
      default:
        return "";
    }
  };

  return (
    <>
      {showConfetti && !reducedMotion && (
        <Suspense fallback={null}>
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
            tweenDuration={3000}
            colors={[colors.PURPLE, colors.PURPLE_LIGHT, colors.PURPLE_LIGHTER, colors.YELLOW_LIGHT]}
          />
        </Suspense>
      )}
      <Container>
        {isOnboarding && <KLBusinessOnboardingProgress currentStep={5} />}
        <motion.div
          variants={reducedMotion ? undefined : successContainerVariants}
          initial="hidden"
          animate="visible"
        >
          <TextIconContainer
            as={motion.div}
            variants={reducedMotion ? undefined : successIconVariants}
          >
            <KLEventAvailableIcon size="large" withGlow={true} />
          </TextIconContainer>
          <Title
            as={motion.div}
            variants={reducedMotion ? undefined : successTitleVariants}
          >
            יש לנו את זה!
          </Title>
          <Subtitle
            as={motion.div}
            variants={reducedMotion ? undefined : successSubtitleVariants}
          >
            {isOnboarding
              ? "ההרשמה של העסק שלכם הסתיימה בהצלחה!"
              : `העדכון של הזמינות שלכם ${getAvailabilityText(availabilityType)} הסתיימה בהצלחה!`}
          </Subtitle>
          {isOnboarding ? (
            <>
              <InfoText
                as={motion.div}
                variants={reducedMotion ? undefined : successInfoVariants}
              >
                עכשיו שולחים את הקישור הבא ללקוחות שלכם, והם יקבעו את ה{pluralTerm}:
              </InfoText>
              <LinkWrapper
                as={motion.div}
                variants={reducedMotion ? undefined : successActionVariants}
              >
                {link}
              </LinkWrapper>
              <motion.div variants={reducedMotion ? undefined : successActionVariants}>
                <KLButton
                  onClick={handleCopyLink}
                  title={copied ? "קישור הועתק!" : "העתק קישור"}
                  secondary
                />
              </motion.div>
            </>
          ) : (
            <InfoText
              as={motion.div}
              variants={reducedMotion ? undefined : successInfoVariants}
            >
              נדאג לעדכן אתכם גם בפעם הבאה :)
            </InfoText>
          )}
          <motion.div variants={reducedMotion ? undefined : successActionVariants}>
            <KLButton onClick={handleGoHome} title="לדף העסק שלי" />
          </motion.div>
        </motion.div>
      </Container>
    </>
  );
};

export default OwnerSuccessScreen;
